import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

export type AccountType = 'demo' | 'live';

const STORAGE_KEY = 'accountType';

/**
 * Which account was last selected.
 *
 * Every reload used to land on Demo, so anyone trading live had to switch back each time —
 * and a mis-set toggle is not a harmless mistake here, since it decides which balance the
 * next order is placed against. Read synchronously in the initialiser so the very first
 * render is already on the right account and nothing loads the wrong one first.
 */
const readStoredAccountType = (): AccountType => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'live' ? 'live' : 'demo';
  } catch {
    // Storage can be unavailable in a private window or a locked-down browser.
    return 'demo';
  }
};

interface AccountTypeContextType {
  accountType: AccountType;
  isDemoAccount: boolean;
  setAccountType: (type: AccountType) => void;
  toggleAccountType: () => void;
}

const AccountTypeContext = createContext<AccountTypeContextType | undefined>(undefined);

export const useAccountType = () => {
  const context = useContext(AccountTypeContext);
  if (context === undefined) {
    throw new Error('useAccountType must be used within an AccountTypeProvider');
  }
  return context;
};

interface AccountTypeProviderProps {
  children: ReactNode;
}

export const AccountTypeProvider: React.FC<AccountTypeProviderProps> = ({ children }) => {
  const [accountType, setAccountType] = useState<AccountType>(readStoredAccountType);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, accountType);
    } catch {
      // Not being able to remember the choice is no reason to stop trading on it.
    }
  }, [accountType]);

  const toggleAccountType = () => {
    setAccountType(prev => prev === 'demo' ? 'live' : 'demo');
  };

  const value: AccountTypeContextType = {
    accountType,
    isDemoAccount: accountType === 'demo',
    setAccountType,
    toggleAccountType,
  };

  return <AccountTypeContext.Provider value={value}>{children}</AccountTypeContext.Provider>;
};
